import { useElapsed, colorState, fmtTimer, fmtTime } from "@/lib/time";
import { AlertTriangle, User, Clock } from "lucide-react";

const STATUS_LABEL = {
  WAITING: "Waiting", ACCEPTED_BY_WAREHOUSE: "Accepted", PREPARED: "Prepared",
  SERVICE_STARTED: "In Service", DELIVERED: "Delivered",
  PACKAGE_NOT_FOUND: "Package Not Found", CUSTOMER_NOT_PRESENT: "Customer Absent",
  PAYMENT_ISSUE: "Payment Issue", VERIFICATION_ISSUE: "Verification",
  PACKAGE_ISSUE: "Package Issue", SYSTEM_ISSUE: "System Issue",
  SUPERVISOR_ASSISTANCE_REQUIRED: "Needs Supervisor", ESCALATED: "Escalated", CANCELLED: "Cancelled",
};

export function TransactionCard({ txn, children, assignedName, subtitle }) {
  const seconds = useElapsed(txn.stage_start);
  const state = txn.stage ? colorState(seconds, txn.sla) : "grey";
  const overdue = state === "red";
  const critical = state === "darkred";
  const isException = txn.is_exception;

  const borderColor = {
    green: "border-l-alert-green", amber: "border-l-alert-amber",
    red: "border-l-alert-red", darkred: "border-l-alert-red", grey: "border-l-alert-grey",
  }[state];

  const timerColor = { green: "#22C55E", amber: "#F59E0B", red: "#FF3B30", darkred: "#FF3B30", grey: "#52525B" }[state];
  const slaLabel = { green: "On track", amber: "At risk", red: "Breached", darkred: "Breached" }[state];

  return (
    <div
      data-testid={`txn-card-${txn.kin_number}`}
      className={`relative bg-card border border-border border-l-4 ${isException ? "border-l-alert-amber" : borderColor} p-4 transition-colors duration-200
        ${critical ? "pulse-critical" : overdue ? "pulse-red" : ""}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="font-mono-t text-xl font-bold tracking-tight text-white truncate">{txn.kin_number}</span>
            {(overdue || critical) && <AlertTriangle size={18} className="text-alert-red shrink-0" />}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5 font-mono-t truncate">{txn.transaction_number}</div>
        </div>
        <div className="text-right shrink-0">
          {txn.stage ? (
            <>
              <span className="font-mono-t font-bold text-3xl leading-none" style={{ color: timerColor }}>
                {fmtTimer(seconds)}
              </span>
              {overdue || critical ? (
                <div className="font-mono-t text-[10px] mt-1" style={{ color: timerColor }}>
                  +{fmtTimer(seconds - txn.sla.overdue_minutes * 60)} OVER
                </div>
              ) : null}
            </>
          ) : (
            <span className="font-display text-sm uppercase tracking-wide text-alert-amber">
              {STATUS_LABEL[txn.current_status]}
            </span>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-3 text-xs text-muted-foreground">
        <span className="flex items-center gap-1"><Clock size={12} /> {subtitle || fmtTime(txn.checked_in_at)}</span>
        <span className="uppercase tracking-wide font-display text-sm text-white/70">{STATUS_LABEL[txn.current_status]}</span>
        {txn.stage && txn.sla && (
          <span className="font-mono-t text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full border"
            style={{ color: timerColor, borderColor: timerColor, backgroundColor: `${timerColor}1A` }}
            data-testid={`sla-badge-${txn.kin_number}`} title={`Warning ${txn.sla.warning_minutes}m · Overdue ${txn.sla.overdue_minutes}m · Critical ${txn.sla.critical_minutes}m`}>
            Target {txn.sla.overdue_minutes}m · {slaLabel}
          </span>
        )}
        {assignedName && (
          <span className="flex items-center gap-1 ml-auto"><User size={12} /> {assignedName}</span>
        )}
      </div>

      {txn.exception_reason && (
        <div className="mt-2 text-xs text-alert-amber border-t border-border pt-2">
          <span className="font-semibold">{txn.exception_type}:</span> {txn.exception_reason}
        </div>
      )}

      {children && <div className="mt-3 flex flex-wrap gap-2">{children}</div>}
    </div>
  );
}

export default TransactionCard;
